import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Drawer,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Avatar,
  useTheme,
  useMediaQuery,
  Tooltip,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Collapse,
  CircularProgress,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/DashboardRounded';
import AssignmentIcon from '@mui/icons-material/AssignmentRounded';
import GroupsIcon from '@mui/icons-material/GroupsRounded';
import PersonIcon from '@mui/icons-material/PersonRounded';
import SettingsIcon from '@mui/icons-material/SettingsRounded';
import LogoutIcon from '@mui/icons-material/LogoutRounded';
import ClassIcon from '@mui/icons-material/ClassRounded';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { motion, AnimatePresence } from 'framer-motion';
import { getTeacherUser } from '../api/teacherApi';
import TeacherSettingsSidebar from './SettingsSidebar';
import { pageTransition } from '../utils/motion';

const drawerWidth = 260;
const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
const apiUrl = (path) => `${API_BASE_URL}${path}`;

// Keep existing hardcoded routes. Add a form_id here when a form has its own page.
// These two mappings use the current Teacher Assignments routes; verify the create path
// against your router if it is named differently in your project.
const FORM_ROUTE_MAP = {
  33: '/teacher/assignments/create', // Create Assignment
  34: '/teacher/assignments', // View Assignment
};

// Fallback route aliases for common existing pages. Module/form names still come from API.
const FORM_NAME_ROUTE_MAP = {
  dashboard: '/teacher/dashboard',
  'teacher dashboard': '/teacher/dashboard',
  'trainer dashboard': '/teacher/dashboard',
  assignments: '/teacher/assignments',
  assignment: '/teacher/assignments',
  'view assignment': '/teacher/assignments',
  'assignment list': '/teacher/assignments',
  students: '/teacher/students',
  student: '/teacher/students',
  'student list': '/teacher/students',
  profile: '/teacher/profile',
  'my profile': '/teacher/profile',
  'teacher profile': '/teacher/profile',
};

const normalizeName = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ');

const getResponseArray = (response) => {
  const data = response?.data;
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.modules)) return data.modules;
  return [];
};

const getFormRoute = (form) => {
  const id = Number(form.formId);
  if (FORM_ROUTE_MAP[id]) return FORM_ROUTE_MAP[id];
  return FORM_NAME_ROUTE_MAP[normalizeName(form.formName)] || null;
};

const getModuleId = (module) => Number(module?.module_id ?? module?.id);
const getModuleName = (module) => module?.module_name || module?.name || module?.title || `Module ${getModuleId(module)}`;
const getFormId = (form) => Number(form?.form_id ?? form?.id);
const getFormName = (form) => form?.form_name || form?.name || form?.title || `Form ${getFormId(form)}`;

const TeacherLayout = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const location = useLocation();

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
  const [modulesWithForms, setModulesWithForms] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [permissionsError, setPermissionsError] = useState(false);
  const [expandedModules, setExpandedModules] = useState({});

  const user = getTeacherUser();
  const roleId = user?.role_id ?? user?.employee_details?.role_id;
  const displayName = user?.employee_name || user?.employee_details?.employee_name || 'Teacher';

  useEffect(() => {
    let cancelled = false;

    const fetchModulesAndPermissions = async () => {
      setPermissionsLoading(true);
      setPermissionsError(false);

      try {
        if (roleId == null || roleId === '') {
          throw new Error('Teacher role_id is missing from getTeacherUser().');
        }

        const token =
          localStorage.getItem('accessToken') ||
          localStorage.getItem('access_token') ||
          localStorage.getItem('token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const [modulesResponse, permissionsResponse] = await Promise.all([
          axios.get(apiUrl('/api/elearning_settings/client/modules/'), { headers }),
          axios.get(apiUrl(`/api/elearning_settings/module-form-permissions/?role_id=${roleId}`), { headers }),
        ]);

        const modules = getResponseArray(modulesResponse);
        const roleRows = getResponseArray(permissionsResponse);
        const roleData = roleRows.find((row) => Number(row?.role_id) === Number(roleId));

        if (!modules.length || !Array.isArray(roleData?.permissions)) {
          throw new Error('Modules or role permissions response has an unexpected format.');
        }

        const permissionLookup = new Map();
        roleData.permissions.forEach((permission) => {
          const key = `${Number(permission?.module_id)}:${Number(permission?.form_id)}`;
          permissionLookup.set(key, permission);
        });

        const allowedModules = modules
          .map((module) => {
            const moduleId = getModuleId(module);
            const moduleName = getModuleName(module);
            const forms = Array.isArray(module?.forms) ? module.forms : [];

            const readableForms = forms
              .map((form) => {
                const formId = getFormId(form);
                const permission = permissionLookup.get(`${moduleId}:${formId}`);
                return {
                  formId,
                  formName: getFormName(form),
                  canRead: permission?.can_read === true,
                  canCreate: permission?.can_create === true,
                  canUpdate: permission?.can_update === true,
                  canDelete: permission?.can_delete === true,
                };
              })
              .filter((form) => form.canRead);

            return { moduleId, moduleName, forms: readableForms };
          })
          .filter((module) => module.forms.length > 0)
          .sort((a, b) => a.moduleName.localeCompare(b.moduleName));

        if (!cancelled) {
          setModulesWithForms(allowedModules);
          // Automatically expand the module containing the current page, if any.
          const activeModule = allowedModules.find((module) =>
            module.forms.some((form) => getFormRoute({ ...form }) === location.pathname),
          );
          if (activeModule) {
            setExpandedModules((previous) => ({ ...previous, [activeModule.moduleId]: true }));
          }
        }
      } catch (error) {
        console.error('Failed to fetch Teacher Panel modules/permissions:', error);
        if (!cancelled) {
          setPermissionsError(true);
          setModulesWithForms([]);
        }
      } finally {
        if (!cancelled) setPermissionsLoading(false);
      }
    };

    fetchModulesAndPermissions();
    return () => {
      cancelled = true;
    };
  }, [roleId, location.pathname]);

  const visibleModules = useMemo(() => modulesWithForms, [modulesWithForms]);

  const handleLogoutClick = () => setLogoutConfirmOpen(true);

  const handleLogoutConfirm = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('access_token');
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('sessionId');
    localStorage.removeItem('client');
    sessionStorage.removeItem('user');
    navigate('/');
  };

  const handleNav = (path) => {
    if (!path) return;
    navigate(path);
    if (isMobile) setMobileOpen(false);
  };

  const toggleModule = (moduleId) => {
    setExpandedModules((previous) => ({ ...previous, [moduleId]: !previous[moduleId] }));
  };

  const activeForm = visibleModules
    .flatMap((module) => module.forms.map((form) => ({ ...form, moduleName: module.moduleName })))
    .find((form) => {
      const route = getFormRoute(form);
      return route && (location.pathname === route || location.pathname.startsWith(`${route}/`));
    });
  const activeLabel = activeForm?.formName || (location.pathname.includes('/submissions') ? 'Submissions' : 'Teacher Panel');

  const sidebarContent = (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: `linear-gradient(180deg, ${theme.palette.primary.main} 0%, ${isDark ? '#15152A' : '#2A2A6E'} 130%)`,
        color: '#fff',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2.75, py: 3 }}>
        <Box
          sx={{
            width: 38,
            height: 38,
            borderRadius: '11px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: alpha('#fff', 0.16),
            backdropFilter: 'blur(6px)',
          }}
        >
          <ClassIcon fontSize="small" />
        </Box>
        <Typography sx={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: '1.05rem' }}>
          Teacher Panel
        </Typography>
      </Box>

      <List sx={{ px: 1.5, flexGrow: 1, overflowY: 'auto' }}>
        {permissionsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
            <CircularProgress size={24} sx={{ color: '#fff' }} />
          </Box>
        ) : permissionsError ? (
          <Typography sx={{ px: 1.5, py: 2, color: alpha('#fff', 0.75), fontSize: '0.85rem' }}>
            Unable to load modules.
          </Typography>
        ) : visibleModules.length ? (
          visibleModules.map((module) => {
            const expanded = Boolean(expandedModules[module.moduleId]);
            const moduleActive = module.forms.some((form) => {
              const route = getFormRoute(form);
              return route && (location.pathname === route || location.pathname.startsWith(`${route}/`));
            });
            const moduleIcon = /assignment/i.test(module.moduleName) ? <AssignmentIcon /> : <FolderOutlinedIcon />;

            return (
              <Box key={module.moduleId} sx={{ mb: 0.5 }}>
                <ListItemButton
                  onClick={() => toggleModule(module.moduleId)}
                  aria-expanded={expanded}
                  sx={{
                    borderRadius: '12px',
                    color: moduleActive || expanded ? '#fff' : alpha('#fff', 0.78),
                    '&:hover': { backgroundColor: alpha('#fff', 0.08) },
                  }}
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: 38 }}>{moduleIcon}</ListItemIcon>
                  <ListItemText
                    primary={module.moduleName}
                    primaryTypographyProps={{ fontWeight: moduleActive || expanded ? 700 : 500, fontSize: '0.92rem' }}
                  />
                  {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                </ListItemButton>

                <Collapse in={expanded} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pl: 2 }}>
                    {module.forms.map((form) => {
                      const route = getFormRoute(form);
                      const active = Boolean(route && (location.pathname === route || location.pathname.startsWith(`${route}/`)));
                      return (
                        <ListItemButton
                          key={`${module.moduleId}-${form.formId}`}
                          disabled={!route}
                          onClick={() => handleNav(route)}
                          title={!route ? 'Add this form route to FORM_ROUTE_MAP to enable navigation' : form.formName}
                          sx={{
                            mb: 0.35,
                            borderRadius: '10px',
                            pl: 2,
                            color: active ? '#fff' : alpha('#fff', route ? 0.76 : 0.42),
                            backgroundColor: active ? alpha('#fff', 0.15) : 'transparent',
                            '&:hover': { backgroundColor: alpha('#fff', 0.08) },
                          }}
                        >
                          <ListItemIcon sx={{ color: 'inherit', minWidth: 32 }}>
                            <DescriptionOutlinedIcon sx={{ fontSize: 18 }} />
                          </ListItemIcon>
                          <ListItemText
                            primary={form.formName}
                            primaryTypographyProps={{ fontSize: '0.84rem', fontWeight: active ? 700 : 400 }}
                          />
                        </ListItemButton>
                      );
                    })}
                  </List>
                </Collapse>
              </Box>
            );
          })
        ) : (
          <Typography sx={{ px: 1.5, py: 2, color: alpha('#fff', 0.75), fontSize: '0.85rem' }}>
            No readable modules available for your role.
          </Typography>
        )}
      </List>

      <Box sx={{ px: 2.5, py: 2.5, borderTop: `1px solid ${alpha('#fff', 0.12)}` }}>
        <Typography variant="caption" sx={{ color: alpha('#fff', 0.55) }}>Signed in as</Typography>
        <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{displayName}</Typography>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          backgroundColor: alpha(theme.palette.background.paper, 0.72),
          backdropFilter: 'blur(12px)',
          borderBottom: `1px solid ${theme.palette.divider}`,
          color: theme.palette.text.primary,
        }}
      >
        <Toolbar sx={{ display: 'flex', justifyContent: 'space-between' }}>
          {isMobile ? (
            <IconButton onClick={() => setMobileOpen(true)} sx={{ color: 'inherit' }}>
              <DashboardIcon />
            </IconButton>
          ) : (
            <Typography sx={{ fontFamily: 'var(--font-display)', fontWeight: 700 }}>{activeLabel}</Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' }, fontWeight: 600 }}>{displayName}</Typography>
            <Avatar
              sx={{
                width: 34,
                height: 34,
                fontWeight: 700,
                fontSize: '0.9rem',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              }}
            >
              {displayName.charAt(0).toUpperCase()}
            </Avatar>
            <Tooltip title="Settings">
              <IconButton onClick={() => setSettingsOpen(true)} sx={{ color: 'inherit' }}><SettingsIcon /></IconButton>
            </Tooltip>
            <Tooltip title="Log out">
              <IconButton onClick={handleLogoutClick} sx={{ color: 'inherit' }}><LogoutIcon /></IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      <Box component="nav" sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}>
        <Drawer
          variant={isMobile ? 'temporary' : 'permanent'}
          open={isMobile ? mobileOpen : true}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{ '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box', border: 'none' } }}
        >
          {sidebarContent}
        </Drawer>
      </Box>

      <Box component="main" sx={{ flexGrow: 1, p: { xs: 2, sm: 3 }, width: { md: `calc(100% - ${drawerWidth}px)` }, backgroundColor: 'background.default' }}>
        <Toolbar />
        <AnimatePresence mode="wait">
          <motion.div key={location.pathname} variants={pageTransition} initial="initial" animate="animate" exit="exit">
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </Box>

      <TeacherSettingsSidebar open={settingsOpen} onClose={() => setSettingsOpen(false)} />

      <Dialog open={logoutConfirmOpen} onClose={() => setLogoutConfirmOpen(false)}>
        <DialogTitle sx={{ fontWeight: 700 }}>Log out?</DialogTitle>
        <DialogContent>
          <DialogContentText>Are you sure you want to log out of your account?</DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setLogoutConfirmOpen(false)} data-app-cancel="true">Cancel</Button>
          <Button onClick={handleLogoutConfirm} color="error" variant="contained">Log out</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TeacherLayout;
